import { GlobalBookingTravelClass } from '@fcom/core/interfaces/global-booking-travel-class.interface';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { isPresent } from '@fcom/core/utils/utils';
import { LocalDate } from '@fcom/core/utils/tz-date';
import { Location } from '@fcom/core-api/interfaces/location-routecff.interface';

import { FinnairMultiCitySearchInterface, FlightSearchParams } from '../interfaces/flight-search.interface';
import { getBookingWidgetCabinClass } from './price-calendar.utils';
import { GlobalBookingFlight } from '../store/store.interface';
import { OndMarketingOffer } from '../../ond-marketing-landing-core/interfaces/ond-marketing-offers.interface';

export const createDeeplinkPathFromFlightSearchParams = (
  { locale, flights, paxAmount, travelClass, promoCode, isAward, isCorporate, tripType }: FlightSearchParams,
  corporateTravelClassLimitEnabled = false
): string => {
  const { adults, c15s, children, infants } = paxAmount;

  const urlParams: FinnairMultiCitySearchInterface = {
    flights: mapFlightsToParams(flights),
    cabin: getBookingWidgetCabinClass(
      travelClass,
      isCorporate,
      corporateTravelClassLimitEnabled,
      tripType === TripType.MULTICITY
    ),
    adults,
    c15s,
    children,
    infants,
    isAward: isAward || undefined,
    promoCode: isAward ? undefined : promoCode,
  };

  Object.keys(urlParams).forEach((key) => {
    if (urlParams[key] === null) {
      delete urlParams[key];
    }
  });

  const params = encodeURIComponent(JSON.stringify(urlParams));
  return `/${locale}/booking/flight-selection?json=${params}`;
};

export const mapFlightsToParams = (
  flights: GlobalBookingFlight[]
): Array<{ origin: string; destination: string; departureDate: string }> => {
  return flights.map(({ origin, destination, departureDate }) => {
    return {
      origin: origin?.locationCode,
      destination: destination?.locationCode,
      departureDate: departureDate.toString(),
    };
  });
};

export const mapOndMarketingOfferToFlightSearchParams = (
  offer: OndMarketingOffer,
  locale: string
): FlightSearchParams | null => {
  const { departureDate, returnDate, originLocationCode, destinationLocationCode } = offer;

  if (!(isPresent(departureDate) && isPresent(originLocationCode) && isPresent(destinationLocationCode))) {
    return null;
  }

  // determine trip type based on  departure and return dates
  const tripType = isPresent(departureDate) && !isPresent(returnDate) ? TripType.ONEWAY : TripType.RETURN;

  // default pax amount
  const paxAmount = {
    adults: 1,
    c15s: 0,
    children: 0,
    infants: 0,
  };

  // create flights array based on trip type
  const flights: GlobalBookingFlight[] = [
    {
      origin: { locationCode: originLocationCode } as Location,
      destination: { locationCode: destinationLocationCode } as Location,
      departureDate: new LocalDate(departureDate),
    },
    ...(tripType === TripType.RETURN
      ? [
          {
            origin: { locationCode: destinationLocationCode } as Location,
            destination: { locationCode: originLocationCode } as Location,
            departureDate: new LocalDate(returnDate),
          },
        ]
      : []),
  ];

  // return the flight search params object
  return {
    tripType,
    paxAmount,
    flights,
    travelClass: GlobalBookingTravelClass.MIXED,
    locale,
    promoCode: null,
  };
};
